import { type UserPermissions } from "../../SettingsPermissions/types/permissions.types.ts"

type ModuleCodes = Record<string, unknown> & {
  features?: Record<string, boolean>
}

/**
 * Several per-module `features` flags are synthesised from the caller's role and
 * only the flag-bearing user serializer emits them, so token refresh and social
 * sign-in return the same permissions object with those keys simply absent —
 * `project_show_budget` and `project_show_progress` among them. Overwriting
 * wholesale turns an absent key into a denial for every surface gated on it, and
 * the slice is persisted, so the loss outlives the reload that would refetch it.
 *
 * A key the payload states wins, including an explicit `false`; only a key it
 * does not mention falls back to what is already held.
 */
export const mergePermissions = (
  incoming: UserPermissions | null | undefined,
  current: UserPermissions | null | undefined
): UserPermissions | null | undefined => {
  if (!incoming) return current
  if (!current) return incoming

  const incomingCodes = incoming.codes as unknown as
    | Record<string, ModuleCodes>
    | undefined
  const currentCodes = current.codes as unknown as
    | Record<string, ModuleCodes>
    | undefined

  if (!incomingCodes || !currentCodes) return incoming
  if (Object.keys(currentCodes).length === 0) return incoming

  const codes = Object.fromEntries(
    Object.entries(incomingCodes).map(([module, moduleCodes]) => {
      const heldFeatures = currentCodes[module]?.features
      if (!heldFeatures || !moduleCodes) return [module, moduleCodes]

      return [
        module,
        {
          ...moduleCodes,
          features: { ...heldFeatures, ...(moduleCodes.features ?? {}) }
        }
      ]
    })
  )

  return { ...incoming, codes: codes as unknown as UserPermissions["codes"] }
}
